/**
 * Rate Limiting Service
 * Previene ataques de fuerza bruta limitando intentos de login
 */

const RATE_LIMIT_CONFIG = {
    MAX_ATTEMPTS: 5,           // Máximo de intentos fallidos
    WINDOW_MS: 15 * 60 * 1000, // Ventana de tiempo: 15 minutos
    BLOCK_DURATION_MS: 30 * 60 * 1000, // Tiempo de bloqueo: 30 minutos
};

class RateLimiter {
    constructor() {
        this.attempts = new Map(); // email -> { count, firstAttempt, blockedUntil }
    }

    /**
     * Verifica si un email está bloqueado
     * @param {string} email - Email del usuario
     * @returns {Object} { blocked: boolean, remainingTime: number }
     */
    isBlocked(email) {
        const record = this.attempts.get(email);

        if (!record || !record.blockedUntil) {
            return { blocked: false, remainingTime: 0 };
        }

        const now = Date.now();

        // Si el bloqueo expiró, limpiar
        if (now > record.blockedUntil) {
            this.attempts.delete(email);
            return { blocked: false, remainingTime: 0 };
        }

        const remainingTime = Math.ceil((record.blockedUntil - now) / 1000 / 60); // minutos
        return { blocked: true, remainingTime };
    }

    /**
     * Registra un intento fallido de login
     * @param {string} email - Email del usuario
     * @returns {Object} { blocked: boolean, remainingAttempts: number, blockedUntil: Date }
     */
    recordFailedAttempt(email) {
        const now = Date.now();
        let record = this.attempts.get(email);

        // Si no existe o la ventana expiró, crear nuevo registro
        if (!record || (now - record.firstAttempt) > RATE_LIMIT_CONFIG.WINDOW_MS) {
            record = {
                count: 1,
                firstAttempt: now,
                blockedUntil: null,
            };
            this.attempts.set(email, record);

            return {
                blocked: false,
                remainingAttempts: RATE_LIMIT_CONFIG.MAX_ATTEMPTS - 1,
                blockedUntil: null,
            };
        }

        // Incrementar contador
        record.count++;

        // Si excede el límite, bloquear
        if (record.count >= RATE_LIMIT_CONFIG.MAX_ATTEMPTS) {
            record.blockedUntil = now + RATE_LIMIT_CONFIG.BLOCK_DURATION_MS;
            this.attempts.set(email, record);

            return {
                blocked: true,
                remainingAttempts: 0,
                blockedUntil: new Date(record.blockedUntil),
            };
        }

        this.attempts.set(email, record);

        return {
            blocked: false,
            remainingAttempts: RATE_LIMIT_CONFIG.MAX_ATTEMPTS - record.count,
            blockedUntil: null,
        };
    }

    /**
     * Limpia el registro de intentos para un email (después de login exitoso)
     * @param {string} email - Email del usuario
     */
    clearAttempts(email) {
        this.attempts.delete(email);
    }

    /**
     * Limpia registros expirados (mantenimiento)
     */
    cleanup() {
        const now = Date.now();

        for (const [email, record] of this.attempts.entries()) {
            // Limpiar si la ventana expiró o el bloqueo expiró
            const windowExpired = (now - record.firstAttempt) > RATE_LIMIT_CONFIG.WINDOW_MS;
            const blockExpired = record.blockedUntil && now > record.blockedUntil;

            if (windowExpired || blockExpired) {
                this.attempts.delete(email);
            }
        }
    }

    /**
     * Obtiene estadísticas del rate limiter
     * @returns {Object} Estadísticas
     */
    getStats() {
        return {
            totalTracked: this.attempts.size,
            blocked: Array.from(this.attempts.values()).filter(r => r.blockedUntil && Date.now() < r.blockedUntil).length,
            config: RATE_LIMIT_CONFIG,
        };
    }
}

// Singleton instance
const rateLimiter = new RateLimiter();

// Cleanup cada 5 minutos
setInterval(() => {
    rateLimiter.cleanup();
}, 5 * 60 * 1000);

export default rateLimiter;
export { RATE_LIMIT_CONFIG };
